import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import ExpensePortalModal from "../modals/ExpensePortalModal";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const PAGE_META = {
  create: {
    title: "Create Expense",
    subtitle: "Add a new expense for your vehicle",
    breadcrumb: "Expense / Create Expense",
  },
  list: {
    title: "Expense List",
    subtitle: "Track, review and export all your vehicle expenses",
    breadcrumb: "Home / Expense / Expense List",
  },
  upload: {
    title: "Upload Expense",
    subtitle: "Upload bulk expenses using Excel or CSV files",
    breadcrumb: "Expense / Upload Expense",
  },
};

export default function ExpensePage({ section = "list" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);

  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const meta = PAGE_META[section] || PAGE_META.list;

  const sidebarItemsLegacy = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/dashboard", onClick: () => navigate("/dashboard") },
      { key: "vehicles", label: "Vehicles", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"), onClick: () => navigate("/admin-dashboard?tab=devices") },
      { key: "live-tracking", label: "Live Tracking", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"), onClick: () => navigate("/admin-dashboard?tab=map") },
      { key: "trips", label: "Trips", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
      { key: "geofences", label: "Geofences", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/geofences", onClick: () => navigate("/geofences") },
      { key: "notifications", label: "Notifications", icon: <NotificationsActiveOutlinedIcon fontSize="small" />, active: location.pathname === "/notifications", onClick: () => navigate("/notifications") },
      { key: "reports", label: "Reports", icon: <ReceiptLongOutlinedIcon fontSize="small" />, active: location.pathname === "/reports", onClick: () => navigate("/reports") },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      { key: "groups", label: "Groups", icon: <Groups2OutlinedIcon fontSize="small" />, active: location.pathname.startsWith("/groups"), onClick: () => navigate("/groups") },
      { key: "reminders", label: "Reminders", icon: <EventNoteOutlinedIcon fontSize="small" />, active: location.pathname === "/reminders", onClick: () => navigate("/reminders") },
      { key: "routes", label: "Routes", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/routes", onClick: () => navigate("/routes") },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        defaultExpanded: true,
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      { key: "settings", label: "Settings", icon: <SettingsOutlinedIcon fontSize="small" />, active: location.pathname === "/profile", onClick: () => navigate("/profile") },
    ],
    [location.pathname, location.search, navigate]
  );
  const sidebarItems = useGlobalSidebarItems();
  void sidebarItemsLegacy;

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "stretch", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
          <Box>
            <Typography sx={{ mt: 0.2, color: "#7c8ba1", fontSize: 13.5 }}>
              {meta.breadcrumb}
            </Typography>
            <Typography sx={{ mt: 1.1, fontSize: { xs: "2rem", md: "2.35rem" }, fontWeight: 900, color: "#0f172a" }}>
              {meta.title}
            </Typography>
            <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15.5 }}>
              {meta.subtitle}
            </Typography>
          </Box>
          <ButtonShell section={section} navigate={navigate} />
        </Box>

        <ExpensePortalModal asPage section={section} />
      </main>
    </div>
  );
}

function ButtonShell({ section, navigate }) {
  if (section === "list") {
    return (
      <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => navigate("/expense/list")}
          style={{
            height: 50,
            minWidth: 118,
            border: "1px solid rgba(148,163,184,0.25)",
            background: "#fff",
            color: "#334155",
            borderRadius: 14,
            fontWeight: 700,
            padding: "0 16px",
            cursor: "pointer",
          }}
        >
          Export
        </button>
        <button
          type="button"
          onClick={() => navigate("/expense/create")}
          style={{
            height: 50,
            minWidth: 144,
            border: "none",
            borderRadius: 14,
            background: "#1d63c8",
            color: "#fff",
            fontWeight: 800,
            padding: "0 18px",
            cursor: "pointer",
            boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
          }}
        >
          Create Expense
        </button>
      </Box>
    );
  }

  if (section === "upload") {
    return (
      <button
        type="button"
        onClick={() => navigate("/expense/list")}
        style={{
          height: 50,
          minWidth: 144,
          border: "none",
          borderRadius: 14,
          background: "#1d63c8",
          color: "#fff",
          fontWeight: 800,
          padding: "0 18px",
          cursor: "pointer",
          boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
        }}
      >
        Expense List
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate("/expense/upload")}
      style={{
        height: 50,
        minWidth: 144,
        border: "none",
        borderRadius: 14,
        background: "#1d63c8",
        color: "#fff",
        fontWeight: 800,
        padding: "0 18px",
        cursor: "pointer",
        boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
      }}
    >
      Upload Expense
    </button>
  );
}
